import React, { useCallback, useEffect, useMemo, useState } from 'react'
import '../parity-dev/cfo/CfoPageShell.css'
import './CfoExperiencesPage.css'
import { ServerApiClient } from '../../data/ServerApiClient'
import type { ServerSite, ServerSiteSummary, ServerSiteWorkbookSummary } from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'
import { CfoBrandMark } from './CfoSiteShared'
import { cfoSiteCanAddExperience } from './cfoExperiences'
import { YESCO_EXPECTED_FRANCHISES, type ExpectedFranchise } from './yescoFranchiseList'

/* Franchise-fleet connect checklist. Built for the YESCO port (61 QBO
   companies connected centrally, one OAuth consent each), but the mechanism
   is generic: an expected-company roster is matched against the connected
   workbooks by QBO company name, and each pending row replays the standard
   CfoExperiencesPage connect flow with the redirect pointed back here so
   the operator can work down the list without re-navigating. */

type LoadStatus = 'loading' | 'ready' | 'error'

interface RosterRow {
  franchise: ExpectedFranchise
  workbook: ServerSiteWorkbookSummary | null
  state: 'connected' | 'attention' | 'pending'
  statusLabel: string
}

const TARGET_SITE_STORAGE_KEY = 'fusegrid_connect_checklist_site'

function normalizeCompanyName(value: string | null | undefined): string {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
}

const shellStyle: React.CSSProperties = {
  display: 'block',
  height: '100%',
  minHeight: 0,
  overflowX: 'hidden',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
}

export function CfoConnectChecklistPage({ api: apiProp }: { api?: ServerApiClient } = {}) {
  const api = useMemo(() => apiProp ?? new ServerApiClient(), [apiProp])

  const [status, setStatus] = useState<LoadStatus>('loading')
  const [sites, setSites] = useState<ServerSite[]>([])
  const [summaries, setSummaries] = useState<ServerSiteSummary[]>([])
  const [targetSiteId, setTargetSiteId] = useState<string>(() => {
    if (typeof window === 'undefined') return ''
    return window.localStorage.getItem(TARGET_SITE_STORAGE_KEY) || ''
  })
  const [connectingProfileId, setConnectingProfileId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [inviteBusyProfileId, setInviteBusyProfileId] = useState<string | null>(null)
  const [inviteLinks, setInviteLinks] = useState<Record<string, { url: string; expiresAt: string }>>({})

  const load = useCallback(async (opts: { quiet?: boolean } = {}) => {
    if (!opts.quiet) setStatus('loading')
    try {
      const session = await ensureApiSessionReady()
      if (!session.token) {
        setSites([])
        setSummaries([])
        setStatus('error')
        return
      }
      const mySites = await api.listMySites()
      const editable = (Array.isArray(mySites) ? mySites : []).filter(cfoSiteCanAddExperience)
      setSites(editable)
      const loaded = await Promise.all(
        (Array.isArray(mySites) ? mySites : [])
          .filter((site) => !!site.slug)
          .map((site) =>
            api.getSiteSummary(site.slug).then(
              (value) => ({ ok: true as const, value }),
              () => ({ ok: false as const, value: null }),
            ),
          ),
      )
      setSummaries(loaded.map((r) => (r.ok ? r.value : null)).filter((s): s is ServerSiteSummary => !!s))
      setStatus('ready')
    } catch {
      if (!opts.quiet) {
        setSites([])
        setSummaries([])
        setStatus('error')
      }
    }
  }, [api])

  useEffect(() => {
    void load()
  }, [load])

  // Bindings (and later qbo_company_name) land seconds-to-minutes after each
  // consent — poll quietly so rows flip without manual refreshes.
  useEffect(() => {
    if (status !== 'ready') return
    const timer = window.setInterval(() => {
      void load({ quiet: true })
    }, 20_000)
    return () => window.clearInterval(timer)
  }, [status, load])

  useEffect(() => {
    if (typeof window === 'undefined') return
    if (targetSiteId) window.localStorage.setItem(TARGET_SITE_STORAGE_KEY, targetSiteId)
  }, [targetSiteId])

  const allWorkbooks = useMemo(() => {
    const rows: ServerSiteWorkbookSummary[] = []
    for (const summary of summaries) rows.push(...(summary.workbooks || []))
    return rows
  }, [summaries])

  const roster = useMemo<RosterRow[]>(() => {
    const byName = new Map<string, ServerSiteWorkbookSummary>()
    for (const workbook of allWorkbooks) {
      // The server names experience workbooks "<Company> CFO Core", and
      // qbo_company_name only fills in after the first sync's statement
      // binding — so freshly connected rows are matched via the workbook
      // name with that suffix stripped.
      const nameSansSuffix = String(workbook.name || '').replace(/\s*CFO Core\s*$/i, '')
      for (const candidate of [workbook.qbo_company_name, workbook.name, nameSansSuffix]) {
        const key = normalizeCompanyName(candidate)
        if (key && !byName.has(key)) byName.set(key, workbook)
      }
    }
    return YESCO_EXPECTED_FRANCHISES.map((franchise) => {
      const workbook = byName.get(normalizeCompanyName(franchise.name)) || null
      if (!workbook || workbook.has_quickbooks_connection === false) {
        return { franchise, workbook, state: 'pending' as const, statusLabel: 'Not connected' }
      }
      const qbo = String(workbook.qbo_connection_status || '')
      if (qbo === 'needs_reconnect' || qbo === 'revoked') {
        return { franchise, workbook, state: 'attention' as const, statusLabel: 'Needs reconnect' }
      }
      return { franchise, workbook, state: 'connected' as const, statusLabel: 'Connected' }
    })
  }, [allWorkbooks])

  const unmatchedConnected = useMemo(() => {
    const expected = new Set(YESCO_EXPECTED_FRANCHISES.map((f) => normalizeCompanyName(f.name)))
    return allWorkbooks.filter((workbook) => {
      if (workbook.has_quickbooks_connection === false) return false
      const names = [workbook.qbo_company_name, workbook.name].map(normalizeCompanyName)
      return names.every((name) => !name || !expected.has(name))
    })
  }, [allWorkbooks])

  const connectedCount = roster.filter((row) => row.state === 'connected').length
  const attentionCount = roster.filter((row) => row.state === 'attention').length

  // For franchisees whose QBO admin isn't the central login (e.g. third-party
  // accountants): mint a governed connection-request link the admin opens to
  // authorize the company themselves.
  const requestInviteLink = useCallback(
    async (row: RosterRow) => {
      setActionError(null)
      setInviteBusyProfileId(row.franchise.profileId)
      try {
        let workbookId = row.workbook?.id
        if (!workbookId) {
          const siteId = targetSiteId || (sites.length === 1 ? sites[0].id : '')
          const { workbook } = await api.createCfoExperience({
            company_name: row.franchise.name,
            site_id: siteId || null,
          })
          workbookId = workbook.id
        }
        const out = await api.createQuickBooksConnectionRequest({
          workbook_id: workbookId,
          note: `Please connect the QuickBooks company "${row.franchise.name}" for YESCO reporting.`,
        })
        setInviteLinks((prev) => ({
          ...prev,
          [row.franchise.profileId]: { url: out.invite_url, expiresAt: out.expires_at },
        }))
        void load({ quiet: true })
      } catch (err: any) {
        setActionError(String(err?.message || err || 'Could not create the request link.'))
      } finally {
        setInviteBusyProfileId(null)
      }
    },
    [api, sites, targetSiteId, load],
  )

  const startConnect = useCallback(
    async (row: RosterRow) => {
      setActionError(null)
      setConnectingProfileId(row.franchise.profileId)
      try {
        const siteId = targetSiteId || (sites.length === 1 ? sites[0].id : '')
        const { workbook } = await api.createCfoExperience({
          company_name: row.franchise.name,
          site_id: siteId || null,
        })
        // Come back to this checklist after the Intuit consent so the operator
        // can continue down the list; status re-derives on mount.
        const redirect = `${window.location.origin}/onboarding/connect-checklist`
        const out = await api.createQuickBooksAuthorizeUrl({
          intent: 'cfo_add_company',
          workbook_id: workbook.id,
          redirect,
        })
        if (!out?.authorize_url) throw new Error('Could not start the QuickBooks connection.')
        window.location.href = out.authorize_url
      } catch (err: any) {
        setConnectingProfileId(null)
        setActionError(String(err?.message || err || 'Something went wrong. Please try again.'))
      }
    },
    [api, sites, targetSiteId],
  )

  if (status === 'loading') {
    return (
      <div className="cfo-shell" style={shellStyle}>
        <div className="cfo-xp-frame"><p className="cfo-xp-muted">Loading connection checklist…</p></div>
      </div>
    )
  }

  if (status === 'error') {
    return (
      <div className="cfo-shell" style={shellStyle}>
        <div className="cfo-xp-frame">
          <CfoBrandMark />
          <h1>Sign in required</h1>
          <p className="cfo-xp-muted">
            Log in first, then return to <code>/onboarding/connect-checklist</code>.
          </p>
          <a className="cfo-btn" href="/">Go to sign in</a>
        </div>
      </div>
    )
  }

  return (
    <div className="cfo-shell" style={shellStyle}>
      <div className="cfo-xp-frame" style={{ maxWidth: 860, margin: '0 auto', padding: '32px 20px 64px' }}>
        <CfoBrandMark />
        <h1 style={{ margin: '18px 0 4px' }}>QuickBooks connection checklist</h1>
        <p className="cfo-xp-muted" style={{ margin: '0 0 18px' }}>
          Connect each company below with its QuickBooks login. After every consent you
          return here automatically — work down the list top to bottom.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', margin: '0 0 18px' }}>
          <strong style={{ fontSize: 15 }}>
            {connectedCount} of {roster.length} connected
            {attentionCount > 0 ? ` · ${attentionCount} need reconnect` : ''}
          </strong>
          <span style={{ flex: '1 1 auto', minWidth: 160, height: 8, borderRadius: 4, background: 'var(--cfo-line, #e3e3e8)', overflow: 'hidden' }}>
            <span
              style={{
                display: 'block',
                width: `${roster.length ? Math.round((connectedCount / roster.length) * 100) : 0}%`,
                height: '100%',
                borderRadius: 4,
                background: 'var(--cfo-accent, #2f6fed)',
              }}
            />
          </span>
          <button className="cfo-btn" type="button" onClick={() => void load()}>
            Refresh
          </button>
        </div>

        {sites.length > 1 ? (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 16px', fontSize: 13 }}>
            <span className="cfo-xp-muted">Add new companies to site:</span>
            <select value={targetSiteId} onChange={(e) => setTargetSiteId(e.target.value)}>
              <option value="">Choose a site…</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>{site.name || site.slug}</option>
              ))}
            </select>
          </label>
        ) : null}

        {actionError ? (
          <p role="alert" style={{ color: 'var(--cfo-danger, #b42318)', margin: '0 0 14px' }}>{actionError}</p>
        ) : null}

        <div role="table" aria-label="Franchise QuickBooks connections">
          {roster.map((row) => (
            <div key={row.franchise.profileId} style={{ borderBottom: '1px solid var(--cfo-line, #ececf1)' }}>
              <div
                role="row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                }}
              >
                <span className="cfo-xp-muted" style={{ width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {row.franchise.profileId}
                </span>
                <span style={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.franchise.name}
                </span>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: '2px 8px',
                    borderRadius: 999,
                    color: row.state === 'connected' ? '#067647' : row.state === 'attention' ? '#b54708' : '#475467',
                    background: row.state === 'connected' ? '#ecfdf3' : row.state === 'attention' ? '#fffaeb' : '#f2f4f7',
                  }}
                >
                  {row.statusLabel}
                </span>
                {row.state !== 'connected' ? (
                  <>
                    <button
                      className="cfo-btn"
                      type="button"
                      disabled={connectingProfileId !== null}
                      onClick={() => void startConnect(row)}
                    >
                      {connectingProfileId === row.franchise.profileId
                        ? 'Opening QuickBooks…'
                        : row.state === 'attention'
                          ? 'Reconnect'
                          : 'Connect'}
                    </button>
                    <button
                      className="cfo-btn"
                      type="button"
                      title="Create a link the company's QuickBooks admin can open to authorize this connection themselves"
                      disabled={inviteBusyProfileId !== null}
                      onClick={() => void requestInviteLink(row)}
                    >
                      {inviteBusyProfileId === row.franchise.profileId ? 'Creating…' : 'Request link'}
                    </button>
                  </>
                ) : null}
              </div>
              {inviteLinks[row.franchise.profileId] ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 12px 10px 52px' }}>
                  <input
                    readOnly
                    value={inviteLinks[row.franchise.profileId].url}
                    onFocus={(e) => e.currentTarget.select()}
                    style={{ flex: '1 1 auto', fontSize: 12, padding: '4px 8px' }}
                  />
                  <button
                    className="cfo-btn"
                    type="button"
                    onClick={() => void navigator.clipboard.writeText(inviteLinks[row.franchise.profileId].url)}
                  >
                    Copy
                  </button>
                  <span className="cfo-xp-muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    expires {new Date(inviteLinks[row.franchise.profileId].expiresAt).toLocaleDateString()}
                  </span>
                </div>
              ) : null}
            </div>
          ))}
        </div>

        {unmatchedConnected.length > 0 ? (
          <div style={{ marginTop: 26 }}>
            <h2 style={{ fontSize: 15, margin: '0 0 6px' }}>Connected, but not on the expected list</h2>
            <p className="cfo-xp-muted" style={{ margin: '0 0 10px', fontSize: 13 }}>
              Usually a QuickBooks company whose name differs from the roster — confirm and
              update the roster (or the franchise map) so reporting keys stay aligned.
            </p>
            {unmatchedConnected.map((workbook) => (
              <div key={workbook.id} style={{ padding: '6px 12px', fontSize: 13 }}>
                {workbook.qbo_company_name || workbook.name}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  )
}
